import { useEffect } from 'react';

const SITE_NAME = 'Student Resource Hub';
const SITE_URL = (process.env.REACT_APP_SITE_URL || '').replace(/\/$/, '');
const DEFAULT_OG_IMAGE = (process.env.REACT_APP_OG_IMAGE || '').replace(/\/$/, '') || '/logo512.png';

export function useSEO({ title, description, canonicalPath, schema = [] }) {
  useEffect(() => {
    document.title = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
  }, [title]);

  useEffect(() => {
    if (!description) return undefined;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', description);
    return undefined;
  }, [description]);

  useEffect(() => {
    if (!canonicalPath) return undefined;
    const base = SITE_URL || window.location.origin;
    const canonicalUrl = canonicalPath.startsWith('http')
      ? canonicalPath
      : `${base}${canonicalPath.startsWith('/') ? '' : '/'}${canonicalPath}`;
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', canonicalUrl);
    return undefined;
  }, [canonicalPath]);

  useEffect(() => {
    const base = SITE_URL || window.location.origin;
    const canonicalUrl = canonicalPath
      ? (canonicalPath.startsWith('http')
        ? canonicalPath
        : `${base}${canonicalPath.startsWith('/') ? '' : '/'}${canonicalPath}`)
      : undefined;

    const ogImage = DEFAULT_OG_IMAGE.startsWith('http')
      ? DEFAULT_OG_IMAGE
      : `${base}${DEFAULT_OG_IMAGE.startsWith('/') ? '' : '/'}${DEFAULT_OG_IMAGE}`;

    const setMetaName = (name, content) => {
      if (!content) return;
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const setMetaProp = (prop, content) => {
      if (!content) return;
      let el = document.querySelector(`meta[property="${prop}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', prop);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
    setMetaProp('og:site_name', SITE_NAME);
    setMetaProp('og:title', fullTitle);
    setMetaProp('og:description', description);
    setMetaProp('og:url', canonicalUrl);
    setMetaProp('og:type', 'website');
    setMetaProp('og:image', ogImage);

    setMetaName('twitter:card', 'summary_large_image');
    setMetaName('twitter:title', fullTitle);
    setMetaName('twitter:description', description);
    setMetaName('twitter:image', ogImage);

    return undefined;
  }, [title, description, canonicalPath]);

  useEffect(() => {
    const prior = Array.from(document.querySelectorAll('[data-seo-schema]'));
    prior.forEach((el) => el.remove());
    if (!schema || schema.length === 0) return undefined;
    schema.forEach((item, idx) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo-schema', 'true');
      script.setAttribute('data-seo-idx', String(idx));
      script.textContent = JSON.stringify(item);
      document.head.appendChild(script);
    });
    return () => {
      Array.from(document.querySelectorAll('[data-seo-schema]')).forEach((el) => el.remove());
    };
  }, [schema]);
}